import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    Autocomplete,
    CircularProgress,
    Alert,
} from '@mui/material';
import { format } from "date-fns";
import { CreateAppointmentDto } from '../../types/Appointment';
import { createAppointment } from '../../services/appointmentService';
import { UserService } from '../../services/authService';
import { getUserPropertyPosts } from '../../services/propertyService';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';

interface ProfileDto {
    id: number;
    username: string;
}

interface PropertyDto {
    id_Property: number;
    title: string;
}

interface CreateAppointmentModalProps {
    open: boolean;
    onClose: () => void;
    defaultDateTime?: string;
    realtorId: number;
    onAppointmentCreated: () => void;
}

const CreateAppointmentModal: React.FC<CreateAppointmentModalProps> = ({
    open,
    onClose,
    defaultDateTime,
    realtorId,
    onAppointmentCreated,
}) => {
    const [formData, setFormData] = useState<CreateAppointmentDto>({
        clientId: 0,
        realtorId,
        propertyId: 0,
        appointmentDate: '',
        appointmentTime: '',
        status: 'Pending',
        notes: '',
    });

    const [userOptions, setUserOptions] = useState<ProfileDto[]>([]);
    const [propertyOptions, setPropertyOptions] = useState<PropertyDto[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const today = format(new Date(), "yyyy-MM-dd");
    const [loading, setLoading] = useState(false);
    const { company } = useCompany();
    const {userToken} = useAuth();
    const resetForm = () => {
        setFormData({
            clientId: 0,
            realtorId,
            propertyId: 0,
            appointmentDate: '',
            appointmentTime: '',
            status: 'Pending',
            notes: '',
        });
    };

    useEffect(() => {
        const loadProperties = async () => {
            try {
            const props = await getUserPropertyPosts(userToken!.user.id, company?.id);
            if (props && Array.isArray(props)) {
                setPropertyOptions(props);
            } else {
                console.warn("No se pudieron cargar las propiedades.");
            }
            } catch (err) {
            console.error("Error al cargar propiedades:", err);
            }
        };

        loadProperties();

        if (defaultDateTime) {
            const [date, time] = defaultDateTime.split('T');
            setFormData((prev) => ({
            ...prev,
            appointmentDate: date,
            appointmentTime: time.slice(0, 5),
            }));
        }
    }, [company?.id, defaultDateTime, userToken]);


    const handleUserSearch = async (value: string) => {
        if (!value) return;
        setLoadingUsers(true);
        const users = await UserService.searchUsernames(value);
        setUserOptions(users);
        setLoadingUsers(false);
    };

    const handleSubmit = async () => {
        if (!formData.clientId || !formData.propertyId) {
            setErrorMessage('Debes seleccionar un cliente y una propiedad.');
            return;
        };
        setLoading(true);
        try {
            const result = await createAppointment(formData);
            if (result.success) {
                resetForm();
                onAppointmentCreated();
                onClose();
            } else {
                setErrorMessage(result.message || 'Ocurrió un error al agendar la cita.');
            }
        } catch (error) {
            setErrorMessage("❌ Error inesperado:");

        } finally {
            setLoading(false);
            resetForm();
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>¡Agenda una nueva cita con un cliente!</DialogTitle>
            <DialogContent dividers sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: { xs: 1.5, sm: 3 },
            }}
            >
                {errorMessage && (
                    <Alert
                        severity="error"
                        onClose={() => setErrorMessage(null)}
                        sx={{ mb: 2 }}
                    >
                        {errorMessage}
                    </Alert>
                )}
                <Autocomplete
                    options={userOptions}
                    getOptionLabel={(option) => option.username}
                    onInputChange={(_, value) => handleUserSearch(value)}
                    onChange={(_, value) => setFormData({ ...formData, clientId: value?.id || 0 })}
                    value={userOptions.find(u => u.id === formData.clientId) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Cliente"
                            placeholder="Buscar cliente"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: loadingUsers ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                            }}
                        />
                    )}
                />

                <TextField
                    select
                    required
                    error={formData.propertyId === 0}
                    label="Propiedad"
                    value={formData.propertyId}
                    onChange={(e) => setFormData({ ...formData, propertyId: Number(e.target.value) })}
                    fullWidth
                >
                    {propertyOptions.map((prop) => (
                        <MenuItem key={prop.id_Property} value={prop.id_Property}>
                            {prop.title}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    type="date"
                    label="Fecha"
                    value={formData.appointmentDate}
                    onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: today }}
                    sx={{
                        '& input': {
                            fontSize: {
                                xs: '0.85rem',
                                sm: '1rem',
                            },
                            padding: {
                                xs: '10px 12px',
                                sm: '14px 12px',
                            },
                        },
                    }}
                />
                <TextField
                    type="time"
                    label="Hora"
                    value={formData.appointmentTime}
                    onChange={(e) => setFormData({ ...formData, appointmentTime: e.target.value })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        '& input': {
                            fontSize: {
                                xs: '0.85rem',
                                sm: '1rem',
                            },
                            padding: {
                                xs: '10px 12px',
                                sm: '14px 12px',
                            },
                        },
                    }}
                />
                <TextField
                    label="Notas"
                    multiline
                    minRows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button sx={{
                    mt: 2,
                    background: "linear-gradient(45deg, #f75151ff, #a32323ff)",
                    boxShadow: "0 4px 20px rgba(188, 24, 24, 0.3)",
                }} variant='contained' onClick={onClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    disabled={loading}
                    sx={{
                        mt: 2,
                        background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                        boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                    }}
                    onClick={handleSubmit}
                >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Confirmar cita"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAppointmentModal;